import { goApi } from './apiService';

export interface ResourceConfig {
    resource: string;
    apiEndpoint: string;
}

export const createCustomGoDataProvider = (
    baseDataProvider: any,
    resourceConfigs: ResourceConfig[],
) => {
    const configMap = new Map(resourceConfigs.map(config => [config.resource, config.apiEndpoint]));

    return {
        ...baseDataProvider,

        getList: async (resource: string, params: any) => {
            const apiEndpoint = configMap.get(resource);
            if (apiEndpoint) {
                const { page, perPage } = params.pagination;
                const filters = params.filter || {};
                const { field, order } = params.sort;
                const query = new URLSearchParams({
                    page: page.toString(),
                    limit: perPage.toString(),
                    ...filters,
                    ...(field ? { orderBy: field } : {}),
                    ...(field && order ? { order: order } : {}),
                }).toString();

                const response = await goApi.get(`${apiEndpoint}?${query}`);

                if (!response?.data?.items || !Array.isArray(response.data.items)) {
                    return { data: [], total: 0 };
                }

                return {
                    data: response.data.items.map((rec: any) => ({
                        ...rec,
                        id: rec.id,
                    })),
                    total: response.data.pagination?.total ?? response.data.items.length,
                };
            }
            return baseDataProvider.getList(resource, params);
        },

        getOne: async (resource: string, params: any) => {
            const apiEndpoint = configMap.get(resource);
            if (apiEndpoint) {
                const response = await goApi.get(`${apiEndpoint}/${params.id}`);
                if (!response?.data) {
                    throw new Error(`Invalid getOne response for ${resource}`);
                }
                return { data: response.data };
            }
            return baseDataProvider.getOne(resource, params);
        },

        delete: async (resource: string, params: any) => {
            const apiEndpoint = configMap.get(resource);
            if (apiEndpoint) {
                await goApi.delete(`${apiEndpoint}/${params.id}`);
                return { data: params.previousData };
            }
            return baseDataProvider.delete(resource, params);
        },

        deleteMany: async (resource: string, params: any) => {
            const apiEndpoint = configMap.get(resource);
            if (apiEndpoint) {
                await Promise.all(
                    params.ids.map((id: string) => goApi.delete(`${apiEndpoint}/${id}`)),
                );
                return { data: params.ids };
            }
            return baseDataProvider.deleteMany(resource, params);
        },

        update: async (resource: string, params: any) => {
            const apiEndpoint = configMap.get(resource);
            if (apiEndpoint) {
                const response = await goApi.put(`${apiEndpoint}/${params.id}`, params.data);
                return { data: response.data };
            }
            return baseDataProvider.update(resource, params);
        },

        create: async (resource: string, params: any) => {
            const apiEndpoint = configMap.get(resource);
            if (apiEndpoint) {
                const response = await goApi.post(apiEndpoint, params.data);
                return { data: response.data };
            }
            return baseDataProvider.create(resource, params);
        },
    };
};
